import { useEffect, useRef, useState } from 'react';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { defaultStagedFinish, finishFields, stagedFinishSource } from 'utils/finishes';
import { getValidCondition } from 'utils/wantListHelpers';

/**
 * Batched display-price fill for want-list rows without a displayPrice, the
 * same pattern the profile Want List tab uses (fetched-ids ref so each entry
 * is requested once). These prices are informational only — they never seed
 * an offer; addable rows always carry quotedPricePoints from offer-candidates.
 */
export const useWantListPriceFill = (
  rows: CollectionList[] | undefined
): Record<number, number> => {
  const [priceMap, setPriceMap] = useState<Record<number, number>>({});
  const fetchedIdsRef = useRef<Set<number>>(new Set());
  const [calculatePrice] = useCalculatePriceMutation();

  useEffect(() => {
    if (!rows || rows.length === 0) return;
    const itemsNeedingPrice = rows.filter(
      (item) => item.displayPrice == null && !fetchedIdsRef.current.has(item.id)
    );
    if (itemsNeedingPrice.length === 0) return;
    itemsNeedingPrice.forEach((item) => fetchedIdsRef.current.add(item.id));

    const fetchPrices = async () => {
      try {
        const result = await calculatePrice({
          cards: itemsNeedingPrice.map((item) => {
            let setDetails = item.acceptableSetDetails;
            if (!setDetails || setDetails.length === 0) {
              setDetails = item.acceptableSets?.map((setName, idx) => ({
                name: setName,
                code:
                  item.acceptableSetCodes?.[idx] ||
                  setName.toLowerCase().replace(/\s+/g, ''),
                cardId: item.acceptedPrintingIds?.[idx] || item.card?.id || 0,
              }));
            }
            const matchingSetDetail =
              setDetails?.find((s) => s.cardId === item.card?.id) || setDetails?.[0];
            const defaultFinish = defaultStagedFinish(
              stagedFinishSource(matchingSetDetail, item.card),
              item
            );
            return {
              wantListId: item.id,
              cardId: item.card?.id?.toString(),
              condition: getValidCondition(item.acceptableConditions?.[0]),
              language: 'EN',
              ...finishFields(defaultFinish),
              set: item.card?.setName,
            };
          }),
        }).unwrap();

        const newPrices: Record<number, number> = {};
        result.cards.forEach((card) => {
          newPrices[card.wantListId] = card.displayPrice;
        });
        setPriceMap((prev) => ({ ...prev, ...newPrices }));
      } catch (error) {
        // Missing prices render the existing "No price data" state — never block
        console.error('Failed to calculate want-list display prices:', error);
      }
    };
    fetchPrices();
  }, [rows, calculatePrice]);

  return priceMap;
};
